import { type IconName } from 'src/components/Icon';
import theme, { Color } from 'src/utils/theme';
import { defineMessages, useIntl } from 'react-intl';
import { Fragment, KeyboardEvent, useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { RecommendationsActivityType } from 'src/api/me/types';
import * as S from './styles';

type Statistic = {
  value: number;
  description: string;
};

export interface LearningStatisticProps {
  backgroundColor: Color;
  icon: IconName;
  iconColor: Color;
  items: Statistic[];
  points: number;
  selected?: boolean;
  title: string;
  onClick: () => void;
  activityKey: RecommendationsActivityType;
}

const msgs = defineMessages({
  totalPoints: {
    id: 'homeScreen_myScore_points_abbreviature_web',
  },
});

const animateConfig = {
  initial: 'collapsed',
  animate: 'open',
  exit: 'collapsed',
  variants: {
    open: { opacity: 1, height: 'auto' },
    collapsed: { opacity: 0, height: 0 },
  },
  transition: {
    duration: 0.8,
    ease: [0.04, 0.62, 0.23, 0.98],
    opacity: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

const LearningStatistic = ({
  backgroundColor,
  icon,
  iconColor,
  items,
  points,
  selected = false,
  title,
  onClick,
  activityKey,
}: LearningStatisticProps) => {
  const intl = useIntl();
  const isDesktop = useMediaQuery({
    minWidth: theme.sz.xxl,
  });
  const isExpanded = useMemo(() => selected || isDesktop, [selected, isDesktop]);
  const onPressEnter = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      /* istanbul ignore else: empty else branch */
      if (event?.key === 'Enter') {
        onClick();
      }
    },
    [onClick],
  );

  const testIdPrefix = `my_progress_${activityKey}`;

  return (
    <S.Wrapper
      $backgroundColor={backgroundColor}
      $active={selected}
      role="button"
      onClick={onClick}
      onKeyUp={onPressEnter}
      aria-expanded={isExpanded}
      tabIndex={0}
      data-testid={testIdPrefix}
    >
      <S.Title>
        <S.IconContainer>
          <S.Icon name={icon} $color={iconColor} />
        </S.IconContainer>
        <S.Column>{title}</S.Column>
        <S.Column data-testid={`${testIdPrefix}_points_label`}>
          {intl.formatMessage(msgs.totalPoints, { points })}
        </S.Column>
      </S.Title>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <S.Row {...animateConfig}>
            {items.map(({ value, description }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={`stat-${index}`}>
                <S.Column $hidden />
                <S.Column data-testid={`${testIdPrefix}_item${index}_value`}>{value}</S.Column>
                <S.Column>{description}</S.Column>
              </Fragment>
            ))}
          </S.Row>
        )}
      </AnimatePresence>
    </S.Wrapper>
  );
};

export default LearningStatistic;
